import React, { useEffect, useState } from 'react';
import Icon from './AppIcon';

/**
 * Glassmorphic notification for generation status:
 * - "slow": Model server may be overloaded (shown after timeout threshold)
 * - "failed": Generation failed, credits not deducted
 * - "cancelled": Generation was cancelled
 */
const GenerationStatusNotification = ({ status, onDismiss }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (status) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [status]);

  const handleDismiss = () => {
    setVisible(false);
    if (onDismiss) onDismiss();
  };

  if (!visible || !status) return null;

  const config = {
    slow: {
      icon: 'Clock',
      title: 'Generation is taking longer than expected',
      message: 'The model server may be overloaded. Your image is still being generated — please wait a moment.',
      borderColor: 'border-orange-500/40',
      glowColor: 'rgba(249, 115, 22, 0.15)',
      iconColor: 'text-orange-400',
      autoDismiss: false,
    },
    failed: {
      icon: 'AlertTriangle',
      title: 'Generation failed',
      message: 'The image could not be generated due to a server error. No credits have been deducted.',
      borderColor: 'border-red-500/40',
      glowColor: 'rgba(239, 68, 68, 0.15)',
      iconColor: 'text-red-400',
      autoDismiss: false,
    },
    cancelled: {
      icon: 'X',
      title: 'Generation cancelled',
      message: 'Image generation was cancelled. No credits have been deducted.',
      borderColor: 'border-white/20',
      glowColor: 'rgba(255, 255, 255, 0.05)',
      iconColor: 'text-white/60',
      autoDismiss: true,
    },
  };

  const c = config[status];
  if (!c) return null;

  // Auto-dismiss cancelled after 5 seconds
  if (c.autoDismiss) {
    setTimeout(() => {
      setVisible(false);
      if (onDismiss) onDismiss();
    }, 5000);
  }

  return (
    <div
      className={`fixed top-6 right-6 z-[9999] max-w-sm w-full animate-slide-in-right`}
    >
      <div
        className={`rounded-2xl p-4 border ${c.borderColor} backdrop-blur-xl`}
        style={{
          background: 'linear-gradient(135deg, rgba(30, 20, 10, 0.92) 0%, rgba(20, 15, 8, 0.95) 100%)',
          boxShadow: `0 8px 32px ${c.glowColor}, 0 0 60px ${c.glowColor}`,
        }}
      >
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 mt-0.5 ${c.iconColor}`}>
            <Icon name={c.icon} size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-white text-sm font-semibold mb-1">{c.title}</h4>
            <p className="text-white/60 text-xs leading-relaxed">{c.message}</p>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-white/40 hover:text-white/70 transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Progress bar for slow status */}
        {status === 'slow' && (
          <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-400 animate-slow-progress"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerationStatusNotification;
